/**
 * DeleteAccountLoginSection — the adaptive sign-in block of the Delete Account
 * page.
 *
 * Rendered only when the visitor must (re-)authenticate before deleting: it
 * shows a short prompt (sign-in vs. re-auth, chosen by session presence) plus
 * the Google/Apple provider buttons. Those buttons are the shared
 * `ProviderSignInButtons` (`components/common/auth`) — the same component the
 * login screen renders through `LoginProviderButtons` — so the pair cannot
 * drift between the two surfaces. Only this page's own `data-qa-id`s
 * (`delete-account-google` / `delete-account-apple`) are passed in per-surface.
 *
 * This is a purely presentational subcomponent: it owns no state and fires no
 * toasts — login outcomes flow through the handlers `useDeleteAccount` was given
 * by the page. On login-success the hook recomputes the staleness gate, which
 * unmounts this section and enables the Delete button.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ProviderSignInButtons } from '@/presentation/components/common/auth/ProviderSignInButtons';

interface DeleteAccountLoginSectionProps {
  /**
   * `true` when the visitor has an established session but it is stale (show the
   * re-auth prompt); `false` when there is no session — signed out entirely or a
   * blocked removing/suspended login (show the sign-in prompt). Derived by the
   * page from `state.auth.user` presence.
   */
  hasSession: boolean;
  /** Disables both buttons while a sign-in flow is in progress. */
  isLoggingIn: boolean;
  onLoginWithGoogle: () => void;
  onLoginWithApple: () => void;
}

export const DeleteAccountLoginSection = ({
  hasSession,
  isLoggingIn,
  onLoginWithGoogle,
  onLoginWithApple,
}: DeleteAccountLoginSectionProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const prompt = hasSession
    ? l10n.deleteAccount.reauthPrompt
    : l10n.deleteAccount.signInPrompt;

  return (
    <div
      // `responsive.auth.buttonsMaxWidth` caps this provider-login column at
      // the Login screen's button width (384px) and `self-center` centers it
      // within the page's stretch-default content column, so the buttons don't
      // sprawl to ~90vw on wide/landscape viewports. The width comes off the
      // shared token that `LoginProviderButtons` also consumes, so the two
      // columns wrapping the shared buttons cannot drift apart.
      className="self-center"
      style={{
        width: '100%',
        maxWidth: responsive.auth.buttonsMaxWidth,
        display: 'flex',
        flexDirection: 'column',
        gap: responsive.spacing.lg,
      }}
    >
      <p
        style={{
          textAlign: 'center',
          fontSize: responsive.font.base,
          color: theme.text.secondary,
          margin: 0,
        }}
      >
        {prompt}
      </p>

      <ProviderSignInButtons
        isLoggingIn={isLoggingIn}
        onLoginWithGoogle={onLoginWithGoogle}
        onLoginWithApple={onLoginWithApple}
        qa={{ googleId: 'delete-account-google', appleId: 'delete-account-apple' }}
      />
    </div>
  );
};
